import React, { useState, useEffect } from 'react';
import {
  Button,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { groupService } from '../../services/group.service';

interface JoinRequestButtonProps {
  groupId: string;
  userRole: 'admin' | 'member' | null;
  isPrivate: boolean;
  onJoinRequestSent?: () => void;
  // 'card' = full-width, dark-filled button used inside group cards.
  variant?: 'default' | 'card';
}

export const JoinRequestButton = ({
  groupId,
  userRole,
  isPrivate,
  onJoinRequestSent,
  variant = 'default'
}: JoinRequestButtonProps) => {
  const { t } = useTranslation('groups');
  const [loading, setLoading] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const isCard = variant === 'card';
  const cardButtonSx = { height: 34, fontSize: 13, width: '100%' };

  // Fetch join request status on component mount
  useEffect(() => {
    const fetchJoinRequestStatus = async () => {
      try {
        setStatusLoading(true);
        const response = await groupService.getUserJoinRequestStatus(groupId);
        setHasPendingRequest(Boolean(response.data.hasJoinRequest));
      } catch (error) {
        // User might not have a join request - that's okay
        setHasPendingRequest(false);
      } finally {
        setStatusLoading(false);
      }
    };

    // Only fetch if user is not already a member
    if (!userRole) {
      fetchJoinRequestStatus();
    } else {
      setStatusLoading(false);
    }
  }, [groupId, userRole]);

  // Don't show button if user is already a member
  if (userRole) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--muted)', fontSize: 13 }}>
        <GroupIcon fontSize="small" />
        {userRole === 'admin' ? t('join.alreadyAdmin') : t('join.alreadyMember')}
      </Box>
    );
  }

  // Show loading while fetching status
  if (statusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isCard ? 34 : undefined }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const handleJoinRequest = async () => {
    try {
      setLoading(true);
      setError(null);
      await groupService.requestToJoinGroup(groupId);
      setHasPendingRequest(true);
      onJoinRequestSent?.();
    } catch (err: any) {
      setError(err.response?.data?.message || t('join.errors.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeRequest = async () => {
    try {
      setLoading(true);
      setError(null);
      await groupService.revokeJoinRequest(groupId);
      setHasPendingRequest(false);
    } catch (err: any) {
      setError(err.response?.data?.message || t('join.errors.revokeFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Show pending request with revoke option
  if (hasPendingRequest) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'var(--warn)', fontSize: 13 }}>
          <ScheduleIcon fontSize="small" />
          {t('join.pendingNotice')}
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <CancelIcon />}
          onClick={handleRevokeRequest}
          disabled={loading}
          size="small"
          sx={isCard ? cardButtonSx : undefined}
        >
          {loading ? t('join.revoking') : t('join.revoke')}
        </Button>
        {error && <Alert severity="error" sx={{ mt: 1, borderRadius: '8px' }}>{error}</Alert>}
      </Box>
    );
  }

  // Rejected requests fall through to the normal join button.

  // Show error state
  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px' }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<PersonAddIcon />}
          onClick={handleJoinRequest}
          disabled={loading}
          sx={isCard ? cardButtonSx : undefined}
        >
          {loading ? <CircularProgress size={20} /> : t('join.tryAgain')}
        </Button>
      </Box>
    );
  }

  // Show join button for users without pending requests
  return (
    <Button
      variant="contained"
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : isCard ? undefined : <PersonAddIcon />}
      onClick={handleJoinRequest}
      disabled={loading}
      sx={
        isCard
          ? { ...cardButtonSx, background: 'var(--fg)', color: 'var(--card)', boxShadow: 'none', '&:hover': { background: 'var(--fg)', opacity: 0.88, boxShadow: 'none' } }
          : undefined
      }
    >
      {loading ? t('join.sending') : isPrivate ? t('join.requestToJoin') : t('join.joinGroup')}
    </Button>
  );
};
